import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { artistsApi } from '../../repositories/artists';
import Swal from 'sweetalert2';
import CSVPreview from './CSVPreview';
import { ValidationError } from './types';

interface CSVUploadProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CSVUpload: React.FC<CSVUploadProps> = ({ onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [parsing, setParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // Helper function to parse and validate CSV
  const parseAndValidateCSV = (csvText: string) => {
    const lines = csvText.split('\n');
    const headers = lines[0]
      .split(',')
      .map(h => h.trim().replace(/^\ufeff/, '').replace(/\*$/, ''));
    console.log("header csv uploads ===>", headers);
    const validRows = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',');
      const row: any = {};
      let hasErrors = false;

      headers.forEach((header, index) => {
        row[header] = values[index]?.trim() || '';
      });

      // Validate required fields
      const requiredFields = ['Artist Name', 'Spotify URI', 'Apple Artist ID', 'ASIN', 'SoundCloud URL', 'TIDAL URL'];
      const missingFields = requiredFields.filter(field => !row[field] || row[field] === '');

      if (missingFields.length > 0) {
        errors.push({
          row: i + 1,
          field: 'Required Fields',
          message: `Missing required fields: ${missingFields.join(', ')}`,
          severity: 'error'
        });
        hasErrors = true;
      }

      // Validate Spotify URI format (accepts full URI or just ID)
      if (row['Spotify URI']) {
        const spotifyValue = row['Spotify URI'];
        const spotifyIdPattern = /^[a-zA-Z0-9]{22}$/;
        const spotifyUriPattern = /^spotify:artist:[a-zA-Z0-9]{22}$/;

        if (!spotifyIdPattern.test(spotifyValue) && !spotifyUriPattern.test(spotifyValue)) {
          errors.push({
            row: i + 1,
            field: 'Spotify URI',
            message: 'Invalid Spotify URI format. Should be either full URI (spotify:artist:25BObiRSDCMwVrBGIVaLIf) or just ID (25BObiRSDCMwVrBGIVaLIf)',
            severity: 'error'
          });
          hasErrors = true;
        }
      }

      // Validate Apple ID format (numbers only)
      if (row['Apple Artist ID'] && !/^\d+$/.test(row['Apple Artist ID'])) {
        errors.push({
          row: i + 1,
          field: 'Apple Artist ID',
          message: 'Apple Artist ID must contain only numbers',
          severity: 'error'
        });
        hasErrors = true;
      }

      // Validate ASIN format (exactly 10 alphanumeric characters)
      if (row['ASIN'] && !/^[a-zA-Z0-9]{10}$/.test(row['ASIN'])) {
        errors.push({
          row: i + 1,
          field: 'ASIN',
          message: 'ASIN must be exactly 10 characters (letters and numbers only)',
          severity: 'error'
        });
        hasErrors = true;
      }


      // Validate URL formats for required URL fields
      const requiredUrlFields = ['SoundCloud URL', 'TIDAL URL'];
      requiredUrlFields.forEach(field => {
        if (row[field] && !/^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(row[field])) {
          errors.push({
            row: i + 1,
            field: field,
            message: `Invalid URL format in ${field}`,
            severity: 'error'
          });
          hasErrors = true;
        }
      });

      // Validate optional URL formats
      const optionalUrlFields = [
        'Instagram', 'TikTok', 'X', 'Facebook', 'Snapchat',
        'Spotify URL', 'Apple URL', 'Amazon URL', 'Deezer URL'
      ];

      optionalUrlFields.forEach(field => {
        if (row[field] && !/^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(row[field])) {
          errors.push({
            row: i + 1,
            field: field,
            message: `Invalid URL format in ${field}`,
            severity: 'error'
          });
          hasErrors = true;
        }
      });

      if (!hasErrors) {
        // Helper function to extract Spotify ID from URI or use as-is
        const extractSpotifyId = (spotifyValue: string) => {
          if (spotifyValue.startsWith('spotify:artist:')) {
            return spotifyValue.replace('spotify:artist:', '');
          }
          return spotifyValue;
        };

        // Transform to backend format
        const artistData = {
          artistName: row['Artist Name'].trim(),
          spotifyURI: extractSpotifyId(row['Spotify URI'].trim()),
          appleID: row["Apple Artist ID"].trim(),
          asin: row['ASIN'].trim(),
          soundcloudURL: row['SoundCloud URL'].trim(),
          tidalURL: row['TIDAL URL'].trim(),
          // Optional fields
          photo: row['Photo'] || '',
          bio: row['Bio'] || '',
          hometown: row['Artist Hometown'] || '',
          currentTown: row['Artist Current Town'] || '',
          country: row['Country'] || '',
          label: row['Label'] || '',
          distributor: row['Distributor'] || '',
          instagram: row['Instagram'] || '',
          tiktok: row['TikTok'] || '',
          twitter: row['X'] || '',
          facebook: row['Facebook'] || '',
          snapchat: row['Snapchat'] || '',
          spotifyURL: row['Spotify URL'] || '',
          appleURL: row['Apple URL'] || '',
          amazonURL: row['Amazon URL'] || '',
          deezerURL: row['Deezer URL'] || ''
        };
        validRows.push(artistData);
      }
    }

    return { validRows, errors };
  };

  // More efficient batch duplicate checking
  const checkDuplicatesBatch = async (validRows: any[]) => {
    setCheckingDuplicates(true);
    const duplicateErrors = [];

    try {
      // Collect all artist names and Spotify URIs
      const artistNames = validRows.map(row => row.artistName);
      const spotifyURIs = validRows.map(row => row.spotifyURI);

      // Make a single API call to check all at once
      const duplicateResponse = await artistsApi.checkDuplicatesBatch({
        artistNames,
        spotifyURIs
      });

      if (duplicateResponse.data.success) {
        const duplicates = duplicateResponse.data.data.duplicates;

        // Map duplicates back to rows
        validRows.forEach((row, index) => {
          const matchingDuplicates = duplicates.filter((dup: any) =>
            dup.artistName.toLowerCase() === row.artistName.toLowerCase() ||
            dup.spotifyURI === row.spotifyURI
          );

          if (matchingDuplicates.length > 0) {
            const duplicateMessages = matchingDuplicates
              .map((dup: any) => `${dup.artistName} (Spotify: ${dup.spotifyURI})`)
              .join(', ');

            duplicateErrors.push({
              row: index + 2,
              field: 'Duplicate Check',
              message: `Artist already exists in your roster: ${duplicateMessages}`,
              severity: 'error'
            });
          }
        });
      }
    } catch (error) {
      console.error('Duplicate check error:', error);
      duplicateErrors.push({
        row: 0,
        field: 'Duplicate Check',
        message: 'Unable to check for duplicates. Please verify manually.',
        severity: 'warning'
      });
    } finally {
      setCheckingDuplicates(false);
    }

    return duplicateErrors;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    console.log(file)
    if (file) {
      setParsing(true);
      setShowPreview(false);
      setSelectedFile(file);

      try {
        // Parse CSV in frontend first
        const csvText = await file.text();
        const { validRows, errors } = parseAndValidateCSV(csvText);

        // Check for duplicates if we have valid rows
        let duplicateErrors: any[] = [];
        if (validRows.length > 0) {
          duplicateErrors = await checkDuplicatesBatch(validRows);
        }

        // Filter out rows that have duplicate errors
        const finalValidRows = validRows.filter((_, index) => {
          return !duplicateErrors.some(error => error.row === index + 2);
        });

        setCsvData(finalValidRows);
        setValidationErrors([...errors, ...duplicateErrors]);
        setShowPreview(true);

        // Store file temporarily (don't upload to backend yet)
        setSelectedFile(file);

      } catch (err: any) {
        console.error('CSV parsing error:', err);
        Swal.fire({
          title: 'CSV Parsing Error',
          text: 'Failed to parse CSV file. Please check the format.',
          icon: 'error'
        });
      } finally {
        setParsing(false);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB limit
  });

  const handleImport = async () => {
    try {
      setUploading(true);

      if (!csvData || csvData.length === 0) {
        Swal.fire({
          title: 'No Data to Import',
          text: 'No valid CSV data available for import.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Show progress modal
      Swal.fire({
        title: 'Importing Artists...',
        html: `
          <div class="text-center">
            <div class="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-sm text-gray-600" id="import-progress">Uploading file...</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Step 1: Upload file
      const progressText = document.getElementById('import-progress');
      // if (progressText) progressText.textContent = 'Uploading file...';

      // if (selectedFile && csvData.length > 0) {
      //   const uploadResponse = await artistsApi.uploadCSV(selectedFile);
      //   if (!uploadResponse.data.success) {
      //     throw new Error('Failed to upload file to storage');
      //   }
      // }

      // Step 2: Import data
      if (progressText) progressText.textContent = 'Importing artists...';

      const response = await artistsApi.importCSV(csvData);
      const result = response.data;

      if (result.success) {
        // Close progress modal and show success
        Swal.close();

        Swal.fire({
          title: 'Import Successful!',
          html: `
            <div class="text-center">
              <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Import Complete</h3>
              <p class="text-sm text-gray-600">
                Successfully imported <strong>${result.data?.totalSuccess || csvData.length}</strong> artists
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Done',
          confirmButtonColor: '#10B981'
        });

        onSuccess();
      } else {
        throw new Error(result.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Import error:', error);

      // Close progress modal
      Swal.close();

      Swal.fire({
        title: 'Import Failed',
        html: `
          <div class="text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Import Failed</h3>
            <p class="text-sm text-gray-600">
              ${error.response?.data?.message || 'Failed to import artists. Please try again.'}
            </p>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'Try Again',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative w-11/12 max-w-6xl p-5 mx-auto overflow-y-auto bg-white border rounded-md shadow-lg top-10" style={{ maxHeight: '60vh' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {uploading ? 'Importing Artists...' :
              parsing ? 'Parsing CSV...' :
                checkingDuplicates ? 'Checking for Duplicates...' :
                  showPreview ? 'CSV Preview' : 'Upload CSV File'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading || parsing || checkingDuplicates}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {(parsing || checkingDuplicates) && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-sm text-gray-600">
              {parsing ? 'Parsing CSV file...' : 'Checking for duplicates...'}
            </p>
          </div>
        )}

        {!showPreview && !parsing && !checkingDuplicates && (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${isDragActive
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-gray-400'
                }`}
            >
              <input  {...getInputProps()} />
              <svg
                className="w-12 h-12 mx-auto text-gray-400 border-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <div className="mt-4">
                <p className="text-lg font-medium text-gray-900">
                  {isDragActive ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  or click to browse files (max 10MB)
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Download the template above to see the required format
                </p>
              </div>
            </div>
          </div>
        )}

        {showPreview && !parsing && !checkingDuplicates && (
          <CSVPreview
            data={csvData}
            errors={validationErrors}
            onImport={handleImport}
            onBack={() => {
              setShowPreview(false);
              setSelectedFile(null);
              setCsvData([]);
              setValidationErrors([]);
            }}
            uploading={uploading}
          />
        )}
      </div>
    </div>
  );
};

export default CSVUpload;